import { addDoc, collection, getDocs, writeBatch } from "firebase/firestore";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import React, { useMemo, useRef, useState } from "react";
import { Close } from "@mui/icons-material";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { firestore, storage } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";
import {
  buildDeaneryDesignation,
  DEANERY_OPTIONS,
  DESIGNATION_TYPE_OPTIONS,
  getDeaneryDesignationPriority,
  ORGANIZATION_OPTIONS,
} from "../../deanery_youth_commission/deanery_data";
import { toTitleCaseName } from "../../../utils/title_case";

interface UploadDeaneryMemberTabProps {
  onSuccess?: () => void;
}

function normalizeUrl(value: string) {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return "";
  }

  if (
    trimmedValue.startsWith("http://") ||
    trimmedValue.startsWith("https://") ||
    trimmedValue.startsWith("/")
  ) {
    return trimmedValue;
  }

  return `https://${trimmedValue}`;
}

async function resequenceDeaneryMembers(deanery: string, targetMemberId: string) {
  const snapshot = await getDocs(collection(firestore, "deanery_members"));
  const orderedDocs = snapshot.docs
    .filter((memberDoc) => memberDoc.data().deanery === deanery)
    .sort((docA, docB) => {
      const priorityA = getDeaneryDesignationPriority(docA.data());
      const priorityB = getDeaneryDesignationPriority(docB.data());

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      const rankA = Number(docA.data().rank || 0);
      const rankB = Number(docB.data().rank || 0);

      if (rankA !== rankB) {
        return rankA - rankB;
      }

      if (docA.id === targetMemberId) {
        return -1;
      }

      if (docB.id === targetMemberId) {
        return 1;
      }

      return String(docA.data().name || "").localeCompare(String(docB.data().name || ""));
    });

  const batch = writeBatch(firestore);
  const timestamp = new Date().toISOString();

  orderedDocs.forEach((memberDoc, index) => {
    batch.update(memberDoc.ref, {
      rank: index + 1,
      updatedAt: timestamp,
    });
  });

  await batch.commit();
}

export default function UploadDeaneryMemberTab({ onSuccess }: UploadDeaneryMemberTabProps) {
  const { showSnackbar } = useFeedback();
  const emptyForm = useMemo(
    () => ({
      deanery: "",
      organization: "Not Applicable",
      designationType: "Director",
      designationDetail: "",
      rank: "",
      name: "",
      place: "",
      img: "",
    }),
    [],
  );
  const [formValues, setFormValues] = useState(emptyForm);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const submitLockRef = useRef(false);

  async function handleSubmit() {
    const rank = Number(formValues.rank);

    if (submitLockRef.current) {
      return;
    }

    if (
      !formValues.deanery ||
      !formValues.name.trim() ||
      !Number.isInteger(rank) ||
      rank <= 0
    ) {
      showSnackbar("Please fill deanery, rank, name, and designation");
      return;
    }

    if (formValues.designationType === "Others" && !formValues.designationDetail.trim()) {
      showSnackbar("Please enter the custom designation");
      return;
    }

    try {
      submitLockRef.current = true;
      setIsSaving(true);
      const timestamp = new Date().toISOString();
      let finalImage = formValues.img.trim();

      if (selectedImage) {
        const storageRef = ref(storage, `deanery_members/${Date.now()}-${selectedImage.name}`);
        await uploadBytes(storageRef, selectedImage);
        finalImage = await getDownloadURL(storageRef);
      }

      const newMemberRef = await addDoc(collection(firestore, "deanery_members"), {
        deanery: formValues.deanery,
        organization: formValues.organization,
        designationType: formValues.designationType,
        designationDetail: formValues.designationDetail.trim(),
        rank,
        name: toTitleCaseName(formValues.name),
        designation: buildDeaneryDesignation(
          formValues.organization as any,
          formValues.designationType as any,
          formValues.designationDetail,
        ),
        place: formValues.place.trim(),
        img: normalizeUrl(finalImage),
        createdAt: timestamp,
        updatedAt: timestamp,
      });

      await resequenceDeaneryMembers(formValues.deanery, newMemberRef.id);

      setFormValues(emptyForm);
      setSelectedImage(null);
      showSnackbar("Deanery member added successfully");
      onSuccess?.();
    } catch (error) {
      console.error("Error adding deanery member", error);
      showSnackbar("Failed to add deanery member");
    } finally {
      setIsSaving(false);
      submitLockRef.current = false;
    }
  }

  return (
    <Box mt={3}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="Deanery"
            value={formValues.deanery}
            onChange={(event) => setFormValues((prev) => ({ ...prev, deanery: event.target.value }))}
          >
            {DEANERY_OPTIONS.map((deanery) => (
              <MenuItem key={deanery} value={deanery}>
                {deanery}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            type="number"
            label="Rank"
            value={formValues.rank}
            onChange={(event) => setFormValues((prev) => ({ ...prev, rank: event.target.value }))}
            inputProps={{ min: 1, step: 1 }}
            helperText="Lower rank appears first inside the same role group. Directors always stay above presidents."
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="Designation"
            value={formValues.designationType}
            onChange={(event) =>
              setFormValues((prev) => ({
                ...prev,
                designationType: event.target.value,
                designationDetail: event.target.value === "Others" ? prev.designationDetail : "",
              }))
            }
          >
            {DESIGNATION_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="Organization"
            value={formValues.organization}
            onChange={(event) => setFormValues((prev) => ({ ...prev, organization: event.target.value }))}
          >
            {ORGANIZATION_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Member Name"
            value={formValues.name}
            onChange={(event) =>
              setFormValues((prev) => ({ ...prev, name: toTitleCaseName(event.target.value) }))
            }
          />
        </Grid>
        <Grid item xs={12}>
          {formValues.designationType === "Others" ? (
            <TextField
              fullWidth
              label="Custom Designation"
              placeholder="Example: Animator"
              value={formValues.designationDetail}
              onChange={(event) =>
                setFormValues((prev) => ({ ...prev, designationDetail: event.target.value }))
              }
            />
          ) : (
            <TextField
              fullWidth
              label="Generated Designation"
              value={buildDeaneryDesignation(
                formValues.organization as any,
                formValues.designationType as any,
                formValues.designationDetail,
              )}
              InputProps={{ readOnly: true }}
              helperText="This is built automatically from organization and designation."
            />
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Place"
            value={formValues.place}
            onChange={(event) => setFormValues((prev) => ({ ...prev, place: event.target.value }))}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Image URL"
            value={formValues.img}
            onChange={(event) => {
              setFormValues((prev) => ({ ...prev, img: event.target.value }));
              if (event.target.value) {
                setSelectedImage(null);
              }
            }}
            helperText="Use either an image URL or upload an image below."
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel>Upload Member Image</FormLabel>
            <input
              type="file"
              accept="image/*"
              style={{
                border: "1px solid rgb(181, 176, 176)",
                width: "96%",
                paddingLeft: 8,
                paddingRight: 8,
                paddingTop: 16,
                paddingBottom: 16,
                borderRadius: 5,
                marginTop: 8,
              }}
              onChange={(event) => {
                if (event.target.files?.[0]) {
                  setSelectedImage(event.target.files[0]);
                  setFormValues((prev) => ({ ...prev, img: "" }));
                }
              }}
            />
            {selectedImage ? (
              <Box mt={2} position="relative" display="inline-block">
                <img
                  src={URL.createObjectURL(selectedImage)}
                  alt="Deanery member preview"
                  style={{
                    maxWidth: "220px",
                    maxHeight: "220px",
                    borderRadius: 8,
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => setSelectedImage(null)}
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    color: "#ffffff",
                    backgroundColor: "rgba(0,0,0,0.5)",
                  }}
                >
                  <Close />
                </IconButton>
              </Box>
            ) : null}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Member"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
